import React, {useEffect, useRef} from 'react';
import * as echarts from 'echarts';

/**
 * 恐惧贪婪指数半圆仪表盘（0-100）：
 * 五段色弧（极度恐惧红/恐惧橙/中性黄/贪婪浅绿/极度贪婪绿）+ 白色指针 + 中心大数字。
 * 暗色适配：色值硬编码自 tokens.css（ECharts 无法读取 CSS 变量）。
 */
export const FngGauge: React.FC<{ value: number }> = ({value}) => {
    const domRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<echarts.ECharts | null>(null);

    // 初始化一次，监听窗口缩放自适应
    useEffect(() => {
        if (!domRef.current) return;
        const chart = echarts.init(domRef.current);
        chartRef.current = chart;
        chart.setOption(buildOption(value));
        const onResize = () => chart.resize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            chart.dispose();
            chartRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 数值变化时仅更新指针与中心数字
    useEffect(() => {
        chartRef.current?.setOption({series: [{data: [{value}]}]});
    }, [value]);

    return <div className="rr-fng-gauge" ref={domRef}/>;
};

/**
 * 构建仪表盘配置：半圆（180°→0°），五段色弧按 alternative.me 官方分段（25/45/55/75）
 */
function buildOption(value: number): echarts.EChartsOption {
    return {
        backgroundColor: 'transparent',
        series: [{
            type: 'gauge',
            startAngle: 180,
            endAngle: 0,
            min: 0,
            max: 100,
            center: ['50%', '72%'],
            radius: '100%',
            pointer: {
                length: '52%',
                width: 3,
                itemStyle: {color: '#e2eaf4'},
            },
            axisLine: {
                lineStyle: {
                    width: 12,
                    color: [
                        [0.25, '#ff5252'],
                        [0.45, '#f5a623'],
                        [0.55, '#e8c34a'],
                        [0.75, '#9ee37d'],
                        [1, '#00d97e'],
                    ],
                },
            },
            axisTick: {show: false},
            splitLine: {show: false},
            axisLabel: {show: false},
            detail: {
                formatter: '{value}',
                color: '#e2eaf4',
                fontSize: 26,
                fontFamily: 'SFMono-Regular, Consolas, monospace',
                offsetCenter: [0, '34%'],
            },
            data: [{value}],
        }],
    };
}
